/**
 * Tonight's pure rules (2.D deliverable 2): the sections, the URL state, the Start number, the
 * attendance set, the suggestion groups and the run-sheet row text. Nothing here reads the DOM,
 * storage or the network.
 */
import type { DocRefV1, SessionAttendancePutV1, SessionItemV1, TonightV1 } from '@codex/contracts';

export const TONIGHT_SECTIONS = [
  'lastTime',
  'characters',
  'strongStart',
  'runSheet',
  'secretsReady',
  'npcsMonsters',
  'locationsLoot',
] as const;
export type TonightSectionKey = (typeof TONIGHT_SECTIONS)[number];

export const SECTION_HEADINGS: Readonly<Record<TonightSectionKey, string>> = {
  lastTime: 'Last time',
  characters: 'Characters',
  strongStart: 'Strong start',
  runSheet: 'Run sheet',
  secretsReady: 'Secrets ready',
  npcsMonsters: 'NPCs and monsters',
  locationsLoot: 'Locations and loot',
};

/** Tonight's URL state: the session shown and the sections folded shut. */
export interface TonightSearch {
  readonly session?: string;
  readonly closed?: readonly TonightSectionKey[];
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isSectionKey = (text: string): text is TonightSectionKey =>
  (TONIGHT_SECTIONS as readonly string[]).includes(text);

/** Keeps a UUID `session` and the known keys of `closed`; drops anything else. */
export function parseTonightSearch(raw: Readonly<Record<string, unknown>>): TonightSearch {
  const out: { session?: string; closed?: readonly TonightSectionKey[] } = {};
  if (typeof raw.session === 'string' && UUID.test(raw.session)) out.session = raw.session;
  if (typeof raw.closed === 'string') {
    const keys = raw.closed.split(',').filter(isSectionKey);
    const closed = TONIGHT_SECTIONS.filter((key) => keys.includes(key));
    if (closed.length > 0) out.closed = closed;
  }
  return out;
}

/** The search with one section's key added to or removed from `closed`, in section order. */
export function toggleClosed(search: TonightSearch, key: TonightSectionKey): TonightSearch {
  const was = search.closed ?? [];
  const next = was.includes(key) ? was.filter((k) => k !== key) : [...was, key];
  const closed = TONIGHT_SECTIONS.filter((k) => next.includes(k));
  const { closed: _dropped, ...rest } = search;
  return closed.length > 0 ? { ...rest, closed } : rest;
}

/** The search as the query string the router writes: `closed` is a comma list. */
export function searchParams(search: TonightSearch): { session?: string; closed?: string } {
  return {
    ...(search.session === undefined ? {} : { session: search.session }),
    ...(search.closed === undefined || search.closed.length === 0
      ? {}
      : { closed: search.closed.join(',') }),
  };
}

/** The number the Start field opens with: the session's own, else the next free one. */
export function startDefaults(tonight: Pick<TonightV1, 'session' | 'nextNumber'>): number {
  return tonight.session?.number ?? tonight.nextNumber;
}

/** An integer of 1 or more, as typed. */
export function validNumber(text: string): boolean {
  return /^\d+$/.test(text.trim()) && Number(text.trim()) >= 1;
}

export interface AttendanceCharacter {
  readonly pc: Pick<DocRefV1, 'id'>;
  readonly present: boolean;
}

/** The whole set, with one row changed, as `sessions.attendance.put` takes it. */
export function attendanceSet(
  characters: readonly AttendanceCharacter[],
  pcId: string,
  present: boolean,
): SessionAttendancePutV1 {
  return {
    rows: characters.map((c) => ({
      pcId: c.pc.id,
      present: c.pc.id === pcId ? present : c.present,
    })),
  };
}

export type SuggestionGroup = 'npcs' | 'monsters' | 'locations' | 'loot';
export type SuggestionVia = TonightV1['suggestions']['npcs'][number]['via'];

export const VIA_TEXT: Readonly<Record<SuggestionVia, string>> = {
  mention: 'Mentioned',
  link: 'Linked',
  embed: 'Embedded',
};

export interface SuggestionRow {
  readonly key: string;
  readonly group: SuggestionGroup;
  readonly doc: DocRefV1;
  readonly via: SuggestionVia;
  readonly viaText: string;
}

export interface SuggestionSection {
  readonly key: 'npcsMonsters' | 'locationsLoot';
  readonly heading: string;
  readonly rows: readonly SuggestionRow[];
}

/** NPCs then monsters; locations then loot; each row with how the run sheet reaches it. */
export function suggestionSections(
  suggestions: TonightV1['suggestions'],
): readonly [SuggestionSection, SuggestionSection] {
  const rows = (group: SuggestionGroup): SuggestionRow[] =>
    suggestions[group].map((s) => ({
      key: `${group}:${s.doc.id}`,
      group,
      doc: s.doc,
      via: s.via,
      viaText: VIA_TEXT[s.via],
    }));
  return [
    {
      key: 'npcsMonsters',
      heading: SECTION_HEADINGS.npcsMonsters,
      rows: [...rows('npcs'), ...rows('monsters')],
    },
    {
      key: 'locationsLoot',
      heading: SECTION_HEADINGS.locationsLoot,
      rows: [...rows('locations'), ...rows('loot')],
    },
  ];
}

/** A row's name: its label, else its target's title, else "Untitled". */
export function itemTitle(item: Pick<SessionItemV1, 'label' | 'document'>): string {
  const label = item.label?.trim();
  if (label) return label;
  const title = item.document?.title.trim();
  return title ? title : 'Untitled';
}

/** The minutes the run sheet plans: a row without a planned time counts for none. */
export function plannedTotal(items: readonly Pick<SessionItemV1, 'estMinutes'>[]): number {
  return items.reduce((sum, item) => sum + (item.estMinutes ?? 0), 0);
}

/** "45 min", "3 h", "3 h 20 min". */
export function formatMinutes(minutes: number): string {
  const n = Math.max(0, Math.round(minutes));
  if (n < 60) return `${n} min`;
  const h = Math.floor(n / 60);
  const m = n % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

export const KIND_TEXT: Readonly<Record<SessionItemV1['kind'], string>> = {
  scene: 'Scene',
  document: 'Page',
  secret: 'Secret',
  note: 'Note',
};

/** True for a run-sheet target in the SRD library. */
export const isReferenceItem = (item: Pick<SessionItemV1, 'document'>): boolean =>
  item.document?.library === 'reference';
